import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Landmark, Globe2, BadgeCheck } from "lucide-react";
import { Page, Eyebrow } from "@/components/PageShell";
import { COMPANY, IMAGES, PROJECTS } from "@/data/site";

const stats = [
  { value: "2014", label: "Operating since" },
  { value: "$1.16B+", label: "Capital campaigns marketed" },
  { value: "4", label: "Continental flagship projects" },
  { value: "1819", label: "Mirabaud heritage" },
];

export default function Home() {
  return (
    <Page testid="home-page">
      <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="home-hero">
        <img
          src={IMAGES.jetDeau}
          alt="Lake Geneva and the Jet d'Eau"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B132B]/60 via-[#0B132B]/80 to-[#0B132B]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 w-full">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <Eyebrow>A Mirabaud Group Operational Branch</Eyebrow>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#F8F9FA] leading-[1.05]">
              Where Swiss precision meets{" "}
              <em className="text-[#E6C687] not-italic">global investment</em> storytelling.
            </h1>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#A0AAB2] max-w-2xl">
              {COMPANY.description}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/projects"
                data-testid="hero-cta-projects"
                className="group inline-flex items-center gap-2 bg-[#C5A059] text-[#0B132B] px-7 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:bg-[#E6C687]"
              >
                View Flagship Projects
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 border border-[rgba(197,160,89,0.4)] text-[#E6C687] px-7 py-3.5 text-sm font-medium tracking-wide transition-all duration-300 hover:border-[#C5A059] hover:bg-[rgba(197,160,89,0.08)]"
              >
                Geneva Headquarters
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-[rgba(197,160,89,0.2)] bg-[#101B3B]" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <span className="font-serif-display text-4xl lg:text-5xl text-[#E6C687]">{s.value}</span>
              <p className="mt-2 font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 items-center" data-testid="home-heritage">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="absolute -top-5 -left-5 w-full h-full border border-[rgba(197,160,89,0.3)]" />
          <img
            src={IMAGES.bankInterior}
            alt="Private banking interior"
            className="relative w-full h-[420px] object-cover"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <Eyebrow>Heritage & Mandate</Eyebrow>
          <h2 className="mt-5 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#F8F9FA] leading-snug">
            The operational marketing arm behind Mirabaud's investment projects.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#A0AAB2]">
            Founded in 2014 on the shores of Lake Geneva, Lac Léman carries two centuries
            of Mirabaud banking discipline into the world of investment marketing. We
            design, position, and deliver the campaigns that connect Mirabaud's projects
            with institutional and private capital across four continents.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              { icon: Landmark, text: "Backed by the Mirabaud Group, Geneva — est. 1819" },
              { icon: Globe2, text: "Campaigns delivered across Latin America, Africa & Europe" },
              { icon: BadgeCheck, text: "Institutional-grade standards on every deliverable" },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-4">
                <Icon size={18} className="text-[#C5A059] mt-1 shrink-0" />
                <span className="text-sm text-[#A0AAB2] leading-relaxed">{text}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/about"
            data-testid="home-cta-about"
            className="group mt-9 inline-flex items-center gap-2 text-sm font-medium text-[#E6C687] transition-colors hover:text-[#F8F9FA]"
          >
            Discover the company
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </section>

      <section className="bg-[#101B3B] border-y border-[rgba(197,160,89,0.2)]" data-testid="home-featured-projects">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <Eyebrow>Selected Work</Eyebrow>
              <h2 className="mt-4 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#F8F9FA]">
                Flagship campaigns
              </h2>
            </div>
            <Link
              to="/projects"
              data-testid="home-cta-all-projects"
              className="group inline-flex items-center gap-2 text-sm font-medium text-[#E6C687] hover:text-[#F8F9FA] transition-colors"
            >
              All projects
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.12 }}
              >
                <Link
                  to="/projects"
                  className="group block border border-[rgba(197,160,89,0.2)] bg-[#162248] card-hover"
                  data-testid={`home-featured-${p.id}`}
                >
                  <div className="overflow-hidden h-52">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover img-fade" />
                  </div>
                  <div className="p-6">
                    <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#C5A059]">
                      {p.region} · {p.year}
                    </span>
                    <h3 className="mt-3 font-serif-display text-xl text-[#F8F9FA] leading-snug">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#6C7A89]">{p.capital}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="home-cta-band">
        <img src={IMAGES.genevaLake} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/90 to-[#0B132B]/70" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-normal text-[#F8F9FA]">
            Quai de l'Île 13, Geneva.
          </h2>
          <p className="mt-4 text-base text-[#A0AAB2]">
            Where every campaign begins — in the heart of Swiss private banking.
          </p>
          <Link
            to="/contact"
            data-testid="home-cta-band-contact"
            className="group mt-8 inline-flex items-center gap-2 bg-[#C5A059] text-[#0B132B] px-7 py-3.5 text-sm font-semibold transition-colors duration-300 hover:bg-[#E6C687]"
          >
            Contact the office
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </Page>
  );
}
